import { type FC, useEffect, useRef } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { supabase } from "@/shared/lib/supabase";
import { uploadSupportingDocument } from "@/shared/utils/uploadSupportingDocument";
import { ReviewScreenLayout } from "@/pages/postloss/modules/components/ReviewScreenLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  FuneralCostForm,
  parseItemToForm,
  validateFuneralCostFields,
  funeralCostToItemFields,
  type FuneralCostFormValues,
} from "./FuneralCostForm";

export const FuneralCostsReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  answers,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { debts, isLoading, isFetching, updateItem, isUpdatingItem, deleteItem, itemActions } =
    useEstateItemActions(estateId);

  const items = filterByCategory(debts, "Ongoing Estate Expenses").filter(
    (i) => i.item_type === "Funeral Home Cost",
  );
  const firstName = person?.first_name ?? "their";
  const hasExtracted = items.some((p) => p.extracted_from_document);
  const hasRedirected = useRef(false);
  const itemsEverFound = useRef(false);

  useEffect(() => {
    if (isLoading || isFetching) return;

    if (items.length > 0) {
      hasRedirected.current = false;
      itemsEverFound.current = true;

      if (answers["funeral_costs_review"] !== "found") {
        goToScreen("funeral_costs_manage", {
          ...answers,
          funeral_costs_review: "found",
          funeral_costs_manage: "yes",
        });
      }
    } else if (
      !hasRedirected.current &&
      !itemsEverFound.current &&
      answers["funeral_costs_review"] !== "found"
    ) {
      hasRedirected.current = true;
      goToScreen("add_funeral_cost", {
        ...answers,
        funeral_costs_review: "none",
      });
    }
  }, [isLoading, isFetching, items.length, answers, goToScreen]);

  const willRedirect =
    !isLoading && !isFetching && items.length > 0 && answers["funeral_costs_review"] !== "found";

  return (
    <ReviewScreenLayout<FuneralCostFormValues>
      items={items}
      isLoading={isLoading}
      isFetching={isFetching}
      isRedirecting={willRedirect || (items.length === 0 && !itemsEverFound.current)}
      screenTitle={
        hasExtracted
          ? "We found these funeral costs in your documents"
          : `Review ${firstName}'s funeral costs`
      }
      screenSubtitle="Review the details and add any others we might have missed."
      itemLabel="funeral costs"
      listTitle="Funeral Costs"
      editDialogTitle="Edit funeral cost"
      deleteDialogTitle="Delete Funeral Cost"
      isUpdatingItem={isUpdatingItem}
      deleteItem={deleteItem}
      itemActions={itemActions}
      onAddAnother={() => {
        goToScreen("add_funeral_cost", {
          ...answers,
          has_funeral_costs: "yes",
          add_funeral_cost: "adding",
        });
      }}
      onContinue={() => onComplete()}
      parseItemToForm={(item) => parseItemToForm(item.item_name, item.balance)}
      renderEditForm={({ form, onChange, existingFileName, onFileRemove }) => (
        <FuneralCostForm
          form={form}
          onChange={onChange}
          existingFileName={existingFileName}
          onFileRemoved={onFileRemove}
        />
      )}
      onSaveEdit={async ({ id, form, fileRemoved }) => {
        if (!estateId) return;

        const validationError = Object.values(validateFuneralCostFields(form))[0];
        if (validationError) throw new Error(validationError);

        let sourceDocumentId: number | undefined;

        if (form.invoiceFile) {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session) return;
          sourceDocumentId = await uploadSupportingDocument(
            form.invoiceFile,
            estateId,
            session.access_token,
            "funeral_invoice",
          );
        }

        const sourceDocUpdate = sourceDocumentId
          ? { source_document_id: sourceDocumentId }
          : fileRemoved
            ? { source_document_id: null }
            : {};

        const fields = funeralCostToItemFields(form);
        await updateItem(id, {
          item_name: form.funeralHomeName.trim(),
          item_type: fields.item_type,
          balance: fields.balance,
          ...sourceDocUpdate,
        });
      }}
    />
  );
};
